// src/context/AdminDataContext.jsx
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebaseConfig";
import { useAdminAccess } from "./AdminAccessContext";

const AdminDataContext = createContext(null);

export function AdminDataProvider({ children }) {
  const { user, adminRecord, isMarketing, loading: accessLoading } = useAdminAccess();
  const [admins, setAdmins] = useState([]);
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  const refreshAll = useCallback(async () => {
    if (!user || !adminRecord || isMarketing) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const [studentsSnap, teachersSnap, groupsSnap, adminsSnap] = await Promise.all([
      getDocs(collection(db, "students")),
      getDocs(collection(db, "teachers")),
      getDocs(collection(db, "groups")),
      getDocs(collection(db, "admin")),
      ]);

      setStudents(studentsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setTeachers(
        teachersSnap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
          tutorType: d.data()?.tutorType || "Teacher",
        }))
      );
      setGroups(groupsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setAdmins(adminsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } finally {
      setLoading(false);
    }
  }, [adminRecord, isMarketing, user]);

  // Fetch once per refresh/session
  useEffect(() => {
    if (accessLoading) return;

    if (!user || !adminRecord || isMarketing) {
      setStudents([]);
      setTeachers([]);
      setGroups([]);
      setAdmins([]);
      setLoading(false);
      return;
    }

    refreshAll();
  }, [accessLoading, adminRecord, isMarketing, refreshAll, user]);

  const value = useMemo(
    () => ({
      students,
      setStudents,
      teachers,
      setTeachers,
      groups,
      setGroups,
      loading,
      refreshAll,
      admins, 
      setAdmins,
    }),
    [students, teachers, groups, admins, loading, refreshAll]
  );

  return <AdminDataContext.Provider value={value}>{children}</AdminDataContext.Provider>;
}

export function useAdminData() {
  const ctx = useContext(AdminDataContext);
  if (!ctx) throw new Error("useAdminData must be used inside <AdminDataProvider>");
  return ctx;
}
